import { useState } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import aideyLogo from '../assets/sidebar-logo.svg';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';

export default function TrialPaywall({ currentUser, onActivated }) {
  const { logout } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasSubscription = !!currentUser?.stripeSubscriptionId;
  // ACCOUNT-4: copy + primary action route off the machine-readable block reason
  // the backend supplies (trial_ended | comp_ended | payment_failed | no_subscription).
  // Fall back to the old subscriptionStatus heuristic if blockReason is absent.
  const blockReason = currentUser?.blockReason
    || ((currentUser?.subscriptionStatus === 'past_due' || currentUser?.subscriptionStatus === 'unpaid') ? 'payment_failed' : null);
  const isPaymentFailure = blockReason === 'payment_failed';

  const COPY = {
    payment_failed: {
      icon: '⚠️',
      title: 'Payment required',
      body: 'We couldn’t process your payment. Please update your payment details to continue using Aidey.',
    },
    comp_ended: {
      icon: '⏰',
      title: 'Your complimentary access has ended',
      body: hasSubscription
        ? 'Your complimentary access period is over. Activate your plan to keep going — your card on file will be charged.'
        : 'Your complimentary access period is over. Choose a plan to keep using Aidey.',
    },
    trial_ended: {
      icon: '⏰',
      title: 'Your free trial has ended',
      body: hasSubscription
        ? 'Your free trial is up. Click below to activate your plan — your card on file will be charged.'
        : 'Your trial has ended and no payment method is on file. Please choose a plan to continue.',
    },
    no_subscription: {
      icon: '🔒',
      title: 'A subscription is required',
      body: 'You don’t have an active subscription. Choose a plan to start using Aidey.',
    },
  };
  const copy = COPY[blockReason] || COPY.trial_ended;

  async function handleActivate() {
    setError('');
    setLoading(true);
    try {
      const res  = await api('/api/billing/activate-subscription', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to activate');
      // Refresh the page so the updated subscriptionStatus is picked up
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  async function handleOpenPortal() {
    setError('');
    setLoading(true);
    try {
      const res  = await api('/api/billing/create-portal-session', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to open billing portal');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem', fontFamily: 'Raleway, sans-serif',
    }}>
      <div style={{
        background: '#fff', borderRadius: 18, padding: '2.5rem 2rem',
        maxWidth: 460, width: '100%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      }}>
        <img src={aideyLogo} alt="Aidey" style={{ height: 40, margin: '0 auto 1.5rem', display: 'block' }} />

        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>
          {copy.icon}
        </div>

        <h2 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: NAVY, margin: '0 0 0.65rem' }}>
          {copy.title}
        </h2>

        <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 1.75rem' }}>
          {copy.body}
        </p>

        {/* BILL-2: owners with a client portal are told their clients cannot reach it while paused. */}
        {currentUser?.hasClientPortal && (
          <p style={{
            background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8,
            padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.55,
            margin: '0 0 1.5rem', textAlign: 'left',
          }}>
            While your account is paused, your client portal is unavailable to your clients. Anyone who visits it is asked to contact you directly.
          </p>
        )}

        {error && (
          <div style={{
            background: '#fde8e8', border: '1px solid #f5a5a5', borderRadius: 8,
            padding: '0.6rem 0.9rem', fontSize: '0.84rem', color: '#A12710',
            marginBottom: '1rem',
          }}>
            ⚠ {error}
          </div>
        )}

        {hasSubscription && !isPaymentFailure ? (
          <button onClick={handleActivate} disabled={loading} style={btnStyle(loading)}>
            {loading ? 'Activating…' : 'Activate my plan'}
          </button>
        ) : isPaymentFailure ? (
          <button onClick={handleOpenPortal} disabled={loading} style={btnStyle(loading)}>
            {loading ? 'Opening…' : 'Update payment details'}
          </button>
        ) : (
          <button onClick={() => { window.location.href = '/plan-select'; }} style={btnStyle(false)}>
            Choose a plan
          </button>
        )}

        <p style={{ color: '#aaa', fontSize: '0.75rem', marginTop: '1rem' }}>
          Questions? Email us at{' '}
          <a href="mailto:support@aideyapp.com" style={{ color: NAVY, fontWeight: 600 }}>support@aideyapp.com</a>
        </p>

        <button
          onClick={() => logout()}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#bbb', fontSize: '0.78rem', marginTop: '0.75rem',
            textDecoration: 'underline', fontFamily: 'Raleway, sans-serif',
          }}
        >
          Log out
        </button>
      </div>
    </div>
  );
}

function btnStyle(disabled) {
  return {
    display: 'block', width: '100%', padding: '0.85rem',
    background: disabled ? '#ccc' : NAVY,
    color: disabled ? '#888' : '#fff',
    border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Poppins, sans-serif', transition: 'background 0.15s',
  };
}
